import { Box, Container, Grid, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';

export default function Sectionthree() {
    const { t } = useTranslation();
    const containerRef = useRef(null);
    const [emojiSprites, setEmojiSprites] = useState([]);
    const [visibleEmojis, setVisibleEmojis] = useState([]);
    const [isVisible, setIsVisible] = useState(false);
    const emojis = useMemo(() => [
        "⚽️", "🏀", "🏈", "🎾", "🏐", "🏉", "🥎", "🏓", "🏸", "⛳️", "🥍", "🏒", "🏏",
        "🥅", "⛸️", "🥌", "🛷", "⛷️", "🏂", "🏋️‍♂️", "🤺", "🤼‍♂️", "🤸‍♀️",
        "⛹️‍♂️", "🚴‍♂️", "🚵‍♀️", "🏇", "🤽‍♂️", "🤾‍♀️", "🏊‍♂️", "🏄‍♂️", "🧗‍♀️",
         "🛹", "🛼", "🏹", "🥊", "🥋", "🎳", "🏆", "🥇", 
        "🎯", "🎱", "🏸", "🏒", "🏑", "🏏", "🥍", "🏹"
    ], []);

    // Intersection Observer to detect when section is visible
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !isVisible) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.2, rootMargin: '0px' }
        );

        const currentRef = containerRef.current;
        if (currentRef && !isVisible) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
            observer.disconnect();
        };
    }, [isVisible]);

    // Generate emoji sprites - only on mount and actual window resize
    useEffect(() => {
        function generateSprites(width, height) {
            const area = Math.max(1, width * height);
            const baseDensity = 1 / 26000; // lower = fewer emojis
            const count = Math.max(10, Math.min(36, Math.floor(area * baseDensity)));
            const sprites = [];
            const placed = [];
            const maxAttemptsPerEmoji = 60;
            const gapPixels = 28; // minimum separation between emojis
            const minFontSize = 24; // increased minimum size
            const maxFontSize = 64; // slightly larger maximum size
            const minOpacity = 1;
            const maxOpacity = 1;

            function overlaps(x, y, radius) {
                for (let i = 0; i < placed.length; i++) {
                    const p = placed[i];
                    const dx = (x - p.x);
                    const dy = (y - p.y);
                    const minDist = radius + p.radius + gapPixels;
                    if (dx * dx + dy * dy < minDist * minDist) return true;
                }
                return false;
            }

            // helper to try place one and record
            function placeEmoji(preferredX, preferredY) {
                const emoji = emojis[Math.floor(Math.random() * emojis.length)];
                const fontSize = minFontSize + Math.floor(Math.random() * Math.max(1, (maxFontSize - minFontSize)));
                const radius = Math.max(10, Math.floor(fontSize * 0.5));
                const minX = radius + 8;
                const maxX = width - radius - 8;
                const minY = radius + 8;
                const maxY = height - radius - 8;
                let attempt = 0;
                let placedOk = false;
                let cx = 0;
                let cy = 0;
                while (attempt < maxAttemptsPerEmoji && !placedOk) {
                    cx = Math.floor(minX + Math.random() * Math.max(1, (maxX - minX)));
                    cy = Math.floor(minY + Math.random() * Math.max(1, (maxY - minY)));
                    if (!overlaps(cx, cy, radius)) placedOk = true;
                    attempt++;
                }
                if (!placedOk) return false;
                placed.push({ x: cx, y: cy, radius });
                const opacity = (minOpacity + Math.random() * (maxOpacity - minOpacity)).toFixed(2);
                sprites.push({ id: `${sprites.length}-${cx}-${cy}`, emoji, left: cx, top: cy, fontSize, opacity });
                return true;
            }

            for (let i = 0; i < count; i++) {
                placeEmoji(null, null);
            }
            return sprites;
        }

        let lastWidth = 0;
        let lastHeight = 0;

        function measureAndGenerate(forceReset = false) {
            const el = containerRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            const width = Math.floor(rect.width);
            const height = Math.floor(rect.height);
            
            // Only regenerate if size actually changed significantly (more than 10px)
            if (Math.abs(width - lastWidth) > 10 || Math.abs(height - lastHeight) > 10 || forceReset) {
                lastWidth = width;
                lastHeight = height;
                setEmojiSprites(generateSprites(width, height));
                setVisibleEmojis([]); // Reset visible emojis when regenerating
            }
        }

        measureAndGenerate(true); // Initial generation
        
        // Debounce resize to prevent constant regeneration on mobile
        let resizeTimer;
        const onResize = () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                window.requestAnimationFrame(() => measureAndGenerate(false));
            }, 250);
        };
        
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
            clearTimeout(resizeTimer);
        };
    }, [emojis]);

    // Animate emojis one by one when section becomes visible
    useEffect(() => {
        if (!isVisible || emojiSprites.length === 0) return;

        const delays = emojiSprites.map((_, index) => index * 80); // 80ms between each emoji
        const timers = [];

        emojiSprites.forEach((sprite, index) => {
            const timer = setTimeout(() => {
                setVisibleEmojis(prev => [...prev, sprite.id]);
            }, delays[index]);
            timers.push(timer);
        });

        return () => {
            timers.forEach(timer => clearTimeout(timer));
        };
    }, [isVisible, emojiSprites]);
    return (
        <>
            <Box ref={containerRef} sx={{
                backgroundColor: "#E4F924",
                position: "relative",
                overflow: "hidden",
                width: "100%",
                minHeight: { xs: "60vh", sm: "100vh" },
                paddingTop: "5rem",
                textAlign: "center",
                paddingBottom: "5rem"
            }}>
                {emojiSprites.map(sprite => {
                    const isVisible = visibleEmojis.includes(sprite.id);
                    return (
                        <span key={sprite.id} style={{
                            position: "absolute",
                            left: `${sprite.left}px`,
                            top: `${sprite.top}px`,
                            transform: isVisible 
                                ? "translate(-50%, -50%) scale(1) rotate(0deg)" 
                                : "translate(-50%, -50%) scale(0) rotate(-180deg)",
                            fontSize: `${sprite.fontSize}px`,
                            opacity: isVisible ? sprite.opacity : 0,
                            userSelect: "none",
                            pointerEvents: "none",
                            zIndex: 0,
                            transition: "all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)",
                            display: "inline-block"
                        }}>
                            {sprite.emoji}
                        </span>
                    );
                })}
                <div style={{ position: "relative", zIndex: 1 }}>
                <Container maxWidth="sm">
                    <Grid container justifyContent={"center"} >
                        <Typography sx={{
                            backgroundColor: "#E4F924",
                            fontSize: { "md": "1.9rem", "xs": "1.2rem" },
                            borderRadius: "10px",
                            fontWeight: "bold", textAlign: "center", padding: "0.3rem",
                            color: "#14213D"
                        }} >{t('sectionthreetittle')}</Typography>
                    </Grid>
                </Container>
                <Grid container justifyContent={"center"} >
                    <Container maxWidth="sm">
                        <Typography sx={{
                            backgroundColor: "#E4F924",
                            textAlign: "center", marginBottom: "1.8rem",
                            // color: "#707070"
                            color: "#000000"
                        }} > {t('sectionthreesubtittle')}</Typography>
                    </Container>
                </Grid>
                <Grid container justifyContent={"center"} >
                    <img src="./assets/home/posasport.png" style={{ width: "65%", minWidth: "340px", height: "auto", marginBottom: "3rem" }} alt="recent Project" />
                </Grid>
                <Grid container justifyContent={"center"} spacing={2}>
                    <Grid item>
                        <Link to="https://play.google.com/store/apps/details?id=com.oxidedigital.posa" target="_blank" style={{ textDecoration: "none" }}>
                            <img
                                src={t('playstore')}
                                alt="Get it on Google Play"
                                style={{ height: "38px" }}
                            />
                        </Link>
                    </Grid>
                    <Grid item>
                        <Link to="https://apps.apple.com/us/app/posa-sport/id6746412980" target="_blank" style={{ textDecoration: "none" }}>
                            <img
                                src={t('appstore')}
                                alt="Download on the App Store"
                                style={{ height: "40px" }}
                            />
                        </Link>
                    </Grid>
                </Grid>
                </div>
            </Box>
        </>
    )

}